import React, { useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface Question {
  text: string;
  type: string;
  search_volume: number;
  cpc: number;
  opportunity_score: number;
  difficulty_score: number;
}

interface Segment {
  type: string;
  questions: Question[];
  total: number;
  avgVolume: number;
  avgCpc: string;
}

interface QuestionWheelVisualProps {
  centerTerm: string;
  data: {
    segments: Segment[];
    totalQuestions: number;
    metrics: {
      avgSearchVolume: number;
      avgCpc: string;
      totalSearchVolume: number;
    };
  };
  onQuestionClick?: (question: Question) => void;
}

export function QuestionWheelVisual({ centerTerm, data, onQuestionClick }: QuestionWheelVisualProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { toast } = useToast();
  const centerX = 900;
  const centerY = 900;
  const innerRadius = 180;
  const outerRadius = 750;

  // Flatten all questions from segments
  const allQuestions = data.segments.flatMap(seg => seg.questions);
  const totalQuestions = allQuestions.length;
  const anglePerQuestion = 360 / totalQuestions;

  const getVolumeColor = (volume: number): string => {
    const maxVolume = Math.max(...allQuestions.map(q => q.search_volume || 0));
    const ratio = volume / (maxVolume || 1);

    // 10 color grades from dark red (0%) to dark green (100%)
    if (ratio >= 0.9) return 'rgb(22, 163, 74)';   // green-600 (90-100%)
    if (ratio >= 0.8) return 'rgb(34, 197, 94)';   // green-500 (80-90%)
    if (ratio >= 0.7) return 'rgb(132, 204, 22)';  // lime-500 (70-80%)
    if (ratio >= 0.6) return 'rgb(163, 230, 53)';  // lime-400 (60-70%)
    if (ratio >= 0.5) return 'rgb(234, 179, 8)';   // yellow-500 (50-60%)
    if (ratio >= 0.4) return 'rgb(251, 146, 60)';  // orange-400 (40-50%)
    if (ratio >= 0.3) return 'rgb(249, 115, 22)';  // orange-500 (30-40%)
    if (ratio >= 0.2) return 'rgb(239, 68, 68)';   // red-500 (20-30%)
    if (ratio >= 0.1) return 'rgb(220, 38, 38)';   // red-600 (10-20%)
    return 'rgb(185, 28, 28)';                      // red-700 (0-10%)
  };

  const polarToCartesian = (angle: number, radius: number) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: centerX + radius * Math.cos(rad),
      y: centerY + radius * Math.sin(rad)
    };
  };

  useEffect(() => {
    if (svgRef.current) {
      const animation = svgRef.current.querySelectorAll('.animate-wheel');
      animation.forEach((el, i) => {
        (el as SVGElement).style.animationDelay = `${i * 0.02}s`;
      });
    }
  }, [data]);

  const handleDownload = () => {
    const svg = svgRef.current;
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 1800;
    canvas.height = 1800;

    img.onload = () => {
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `${centerTerm}-wheel-visualization.png`;
      downloadLink.href = pngFile;
      downloadLink.click();

      toast({
        title: 'Success',
        description: 'Wheel visualization downloaded as PNG',
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col items-center">
          <div className="mb-4 flex justify-end w-full">
            <Button onClick={handleDownload} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download PNG
            </Button>
          </div>

          <svg
            ref={svgRef}
            width="1800"
            height="1800"
            viewBox="0 0 1800 1800"
            className="mx-auto"
            style={{ maxWidth: '100%', height: 'auto', userSelect: 'none' }}
          >
            {/* Outer volume rings */}
            {allQuestions.map((question, index) => {
              const angle = index * anglePerQuestion;
              const startAngle = angle;
              const endAngle = angle + anglePerQuestion - 1;

              const start = polarToCartesian(startAngle, outerRadius);
              const end = polarToCartesian(endAngle, outerRadius);
              const innerStart = polarToCartesian(startAngle, outerRadius - 30);
              const innerEnd = polarToCartesian(endAngle, outerRadius - 30);

              const largeArcFlag = anglePerQuestion > 180 ? 1 : 0;

              const pathData = [
                `M ${start.x} ${start.y}`,
                `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
                `L ${innerEnd.x} ${innerEnd.y}`,
                `A ${outerRadius - 30} ${outerRadius - 30} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
                'Z'
              ].join(' ');

              return (
                <path
                  key={`ring-${index}`}
                  d={pathData}
                  fill={getVolumeColor(question.search_volume || 0)}
                  opacity="0.7"
                  className="animate-wheel cursor-pointer hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Ring clicked:', question.text);
                    onQuestionClick?.(question);
                  }}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                />
              );
            })}

            {/* Question segments */}
            {allQuestions.map((question, index) => {
              const angle = index * anglePerQuestion + anglePerQuestion / 2;

              const lineStart = polarToCartesian(angle, innerRadius + 10);
              const lineEnd = polarToCartesian(angle, outerRadius - 45);

              // Determine text position and rotation based on which side of the circle
              let textRadius: number;
              let rotation: number;
              let textAnchor: "start" | "end";

              // Left side of circle (180° to 360°): start from inside, read outward
              if (angle > 180 && angle <= 360) {
                textRadius = innerRadius + 460;  // Moved 30px more (was 430)
                rotation = angle + 90;
                textAnchor = "start";
              }
              // Right side of circle (0° to 180°): end at inside position
              else {
                textRadius = innerRadius + 460;  // Moved 30px more (was 430)
                rotation = angle - 90;
                textAnchor = "end";
              }

              const textPos = polarToCartesian(angle, textRadius);

              return (
                <g key={`question-${index}`} className="animate-wheel">
                  {/* Radial line */}
                  <line
                    x1={lineStart.x}
                    y1={lineStart.y}
                    x2={lineEnd.x}
                    y2={lineEnd.y}
                    stroke="#d1d5db"
                    strokeWidth="0.5"
                    opacity="0.2"
                  />

                  {/* Question text - positioned radially */}
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    fontSize="14"
                    fill="#374151"
                    textAnchor={textAnchor}
                    transform={`rotate(${rotation}, ${textPos.x}, ${textPos.y})`}
                    className="font-normal cursor-pointer hover:font-bold transition-all"
                    opacity="0.85"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Text clicked:', question.text);
                      onQuestionClick?.(question);
                    }}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  >
                    <tspan>{question.text}</tspan>
                  </text>

                  {/* Volume indicator dots */}
                  <circle
                    cx={polarToCartesian(angle, outerRadius - 60).x}
                    cy={polarToCartesian(angle, outerRadius - 60).y}
                    r="2.5"
                    fill={getVolumeColor(question.search_volume || 0)}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Dot clicked:', question.text);
                      onQuestionClick?.(question);
                    }}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  />
                </g>
              );
            })}

            {/* Category labels */}
            {data.segments.map((segment, segmentIndex) => {
              // Find first and last question indices for this segment
              let currentIndex = 0;
              for (let i = 0; i < segmentIndex; i++) {
                currentIndex += data.segments[i].questions.length;
              }

              const firstQuestionIndex = currentIndex;
              const lastQuestionIndex = currentIndex + segment.questions.length - 1;

              const midAngle = (firstQuestionIndex * anglePerQuestion + lastQuestionIndex * anglePerQuestion) / 2 + anglePerQuestion / 2;
              const labelPos = polarToCartesian(midAngle, outerRadius + 100);  // Moved further out (was 40)

              return (
                <text
                  key={`category-${segmentIndex}`}
                  x={labelPos.x}
                  y={labelPos.y}
                  fontSize="20"
                  fontWeight="bold"
                  fill="#1f2937"
                  textAnchor="middle"
                  className="uppercase tracking-widest animate-wheel"
                >
                  {segment.type}
                </text>
              );
            })}

            {/* Center circle */}
            <circle
              cx={centerX}
              cy={centerY}
              r={innerRadius}
              fill="#3b82f6"
              filter="url(#glow)"
            />

            {/* Center text */}
            <text
              x={centerX}
              y={centerY - 40}
              fontSize="14"
              fill="#ffffff"
              textAnchor="middle"
              className="font-medium"
            >
              Search Volume
            </text>
            <text
              x={centerX}
              y={centerY - 10}
              fontSize="32"
              fontWeight="bold"
              fill="#ffffff"
              textAnchor="middle"
            >
              {(data.metrics?.totalSearchVolume || 0).toLocaleString()}
            </text>
            <text
              x={centerX}
              y={centerY + 20}
              fontSize="18"
              fontWeight="bold"
              fill="#ffffff"
              textAnchor="middle"
            >
              {centerTerm}
            </text>
            <text
              x={centerX}
              y={centerY + 45}
              fontSize="14"
              fill="#ffffff"
              textAnchor="middle"
              opacity="0.9"
            >
              {data.totalQuestions} Questions
            </text>
            <text
              x={centerX}
              y={centerY + 65}
              fontSize="12"
              fill="#ffffff"
              textAnchor="middle"
              opacity="0.8"
            >
              Avg CPC: ${Number(data.metrics?.avgCpc || 0).toFixed(2)}
            </text>

            {/* Glow filter */}
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
          </svg>

          <style>
            {`
              @keyframes wheelFadeIn {
                from {
                  opacity: 0;
                  transform: scale(0.9);
                }
                to {
                  opacity: 1;
                  transform: scale(1);
                }
              }

              .animate-wheel {
                animation: wheelFadeIn 0.6s ease-out forwards;
                opacity: 0;
              }
            `}
          </style>

          {/* Legend */}
          <div className="mt-6 grid grid-cols-5 gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(22, 163, 74)' }} />
              <span>90-100%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(34, 197, 94)' }} />
              <span>80-90%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(132, 204, 22)' }} />
              <span>70-80%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(163, 230, 53)' }} />
              <span>60-70%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(234, 179, 8)' }} />
              <span>50-60%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(251, 146, 60)' }} />
              <span>40-50%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(249, 115, 22)' }} />
              <span>30-40%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(239, 68, 68)' }} />
              <span>20-30%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(220, 38, 38)' }} />
              <span>10-20%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(185, 28, 28)' }} />
              <span>0-10%</span>
            </div>
          </div>

          <div className="mt-4 text-sm text-muted-foreground text-center">
            Click on any segment or question text to see details
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
